import React, { useEffect, useState } from 'react';
import Alert from 'antd/lib/alert';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';

import { getCore } from 'cvat-core-wrapper';
import { plural, t } from 'utils/i18n';

interface Props {
    projectId: number;
    referenceId: number;
    linked: number[];
    onClose(): void;
    onLinked(): void;
}

interface Option {
    id: number;
    name: string;
    size: number;
}

const core = getCore();

// the photos join one more task of the project
export default function LinkTaskModal({
    projectId, referenceId, linked, onClose, onLinked,
}: Props): JSX.Element {
    const [options, setOptions] = useState<Option[] | null>(null);
    const [chosen, setChosen] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        core.server.request(`${core.config.backendAPI}/tasks?project_id=${projectId}&page_size=100`, { method: 'GET' })
            .then((response: { data: { results: Option[] } }) => {
                if (!cancelled) setOptions(response.data.results.filter((item) => !linked.includes(item.id)));
            })
            .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
        return () => { cancelled = true; };
    }, [projectId]);

    const link = async (): Promise<void> => {
        if (chosen === null) return;
        setBusy(true);
        setError(null);
        try {
            await core.server.request(`${core.config.backendAPI}/references/${referenceId}`, {
                method: 'PATCH', data: { tasks: [...linked, chosen] },
            });
            onLinked();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            visible
            title={t('Link a task')}
            okText={t('Link')}
            onOk={link}
            onCancel={onClose}
            okButtonProps={{ disabled: chosen === null, loading: busy }}
        >
            {options && !options.length ? (
                <Text type='secondary'>{t('Every task of the project is already linked to these photos.')}</Text>
            ) : (
                <Select
                    className='cvat-task-page-link-select'
                    aria-label={t('Task')}
                    placeholder={t('Choose a task')}
                    loading={options === null}
                    virtual={false}
                    value={chosen ?? undefined}
                    onChange={(value: number) => setChosen(value)}
                >
                    {(options ?? []).map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                            {`${item.name} · ${plural(item.size, 'drawing')}`}
                        </Select.Option>
                    ))}
                </Select>
            )}
            {error && <Alert type='error' showIcon message={error} className='cvat-task-page-modal-note' />}
        </Modal>
    );
}
